import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import ExtensionCommentThread from './ExtensionCommentThread';
import * as styles from './ExtensionMagneticWall.module.scss';
import type { DocumentType } from '../../../graphql/generated/gql';
import { gql } from '../../../graphql/generated/gql';
import type { ExtensionCommentThread_ThreadFragment } from '../../../graphql/generated/graphql';
import { isCommentOrPublicCommentThread } from '../../../utils/post/threadUtils';

const AvatarHeight = 40;

const POST_FRAGMENT = gql(`
  fragment ExtensionMagneticWall_post on Post {
    threads {
      id
      ...ExtensionCommentThread_thread
    }
    ...ExtensionCommentThread_post
  }
`);

const moveThreadClass = (quill: Quill, threadId: string | null) => {
  const className = 'thread-open';
  _.forEach(quill.root.querySelectorAll(`.editor .${className}`), (node) => {
    node.classList.remove(className);
  });

  if (threadId) {
    _.forEach(
      quill.root.querySelectorAll(`.editor .comment-${threadId}`),
      (node) => {
        node.classList.add(className);
      },
    );
  }
};

interface MagneticWallProps {
  quill: Quill | null;
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ExtensionMagneticWall = ({ quill, post }: MagneticWallProps) => {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [positions, setPositions] = useState<
    {
      thread: ExtensionCommentThread_ThreadFragment;
      node: Element;
      top: number;
    }[]
  >([]);

  useLayoutEffect(() => {
    if (quill) moveThreadClass(quill, activeThreadId);
  }, [quill, activeThreadId]);

  const updatePositions = useCallback(
    (localQuill: Quill) => {
      if (!localQuill) {
        setPositions([]);
        return;
      }

      const container = localQuill.root;

      const containerTop = container.getBoundingClientRect().top;

      const newPositions = _.compact(
        post.threads.map((thread) => {
          const node = container.querySelector(`.comment-${thread.id}`);
          if (!node || !isCommentOrPublicCommentThread(thread)) return null;
          if (!(node instanceof HTMLElement) || node.offsetParent === null) {
            return null;
          }
          const rect = node.getBoundingClientRect();
          return { node, thread, top: rect.top - containerTop };
        }),
      ).toSorted((a, b) => a.top - b.top);

      setPositions(
        newPositions.map((position, i) => {
          const prevPosition = newPositions[i - 1];
          return prevPosition
            ? {
                ...position,
                top: Math.max(
                  position.top,
                  Number(prevPosition.top) + AvatarHeight,
                ),
              }
            : position;
        }),
      );
    },
    [post.threads],
  );

  const debouncedUpdatePositions = useDebouncedCallback(updatePositions, 200);

  useEffect(() => {
    if (!quill) return undefined;
    updatePositions(quill);
    debouncedUpdatePositions(quill);

    const listener = () => {
      debouncedUpdatePositions(quill);
    };

    quill.on('text-change', listener);
    return () => {
      quill.off('text-change', listener);
    };
  }, [updatePositions, debouncedUpdatePositions, quill]);

  useEffect(() => {
    if (!quill) return undefined;
    const listener = (e: MouseEvent) => {
      if (e.target instanceof Element) {
        const commentNode = e.target.closest('[class*="comment-"]');
        if (commentNode) {
          for (const c of commentNode.classList) {
            if (c.startsWith('comment-')) {
              setActiveThreadId(c.replace(/^comment-/, ''));
              return;
            }
          }
        }
      }
      setActiveThreadId(null);
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) return undefined;

    const observer = new ResizeObserver(() => debouncedUpdatePositions(quill));
    observer.observe(quill.root);

    return () => {
      observer.disconnect();
    };
  }, [debouncedUpdatePositions, quill]);

  return (
    <div className={styles.container}>
      {positions.map((position) => (
        <div
          key={position.thread.id}
          className={styles.comment}
          style={{ top: position.top }}
        >
          <ExtensionCommentThread
            thread={position.thread}
            post={post}
            activeThreadId={activeThreadId}
            onActiveThreadIdChange={setActiveThreadId}
          />
        </div>
      ))}
    </div>
  );
};

export default ExtensionMagneticWall;
